import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';

import { API_URL } from '../config/api.config';
import { ApiResponse } from '../models/api.model';
import { Plan, PlanEmpresa, PlanRequest } from '../models/plan.model';
import {
  PagedResponse,
  PaginationRequest,
} from '../../shared/data-table-paginado/data-table-paginado.models';

@Injectable({ providedIn: 'root' })
export class PlanService {
  private readonly http = inject(HttpClient);
  private readonly resourceUrl = `${API_URL}/planes`;

  getAll(): Observable<Plan[]> {
    return this.http
      .get<ApiResponse<Plan[]>>(this.resourceUrl)
      .pipe(map((response) => response.data ?? []));
  }

  getPage(request: PaginationRequest): Observable<ApiResponse<PagedResponse<Plan>>> {
    let params = new HttpParams().set('page', request.page).set('pageSize', request.pageSize);
    if (request.search?.trim()) params = params.set('search', request.search.trim());
    if (request.status) params = params.set('status', request.status);

    return this.http.get<ApiResponse<PagedResponse<Plan>>>(`${this.resourceUrl}/paginado`, {
      params,
    });
  }

  getEmpresas(planId: string): Observable<PlanEmpresa[]> {
    return this.http
      .get<ApiResponse<PlanEmpresa[]>>(`${this.resourceUrl}/${planId}/empresas`)
      .pipe(map((response) => response.data ?? []));
  }

  create(request: PlanRequest): Observable<Plan> {
    return this.http
      .post<ApiResponse<Plan>>(this.resourceUrl, request)
      .pipe(map((response) => this.requireData(response)));
  }

  update(id: string, request: PlanRequest): Observable<Plan> {
    return this.http
      .put<ApiResponse<Plan>>(`${this.resourceUrl}/${id}`, request)
      .pipe(map((response) => this.requireData(response)));
  }

  toggleStatus(id: string): Observable<Plan> {
    return this.http
      .patch<ApiResponse<Plan>>(`${this.resourceUrl}/${id}/status`, {})
      .pipe(map((response) => this.requireData(response)));
  }

  delete(id: string): Observable<void> {
    return this.http.delete(`${this.resourceUrl}/${id}`).pipe(map(() => undefined));
  }

  private requireData<T>(response: ApiResponse<T>): T {
    if (response.data === null) throw new Error(response.message ?? 'La API no devolvió datos.');
    return response.data;
  }
}
